<template>
  <div
    role="main"
    aria-label="Création de bon de retour inter-agence"
    tabindex="-1"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        label="Chargement en cours"
      />
    </div>
    <b-card aria-label="Formulaire de saisie de référence de colis">
      <b-form-group
        label="Référence du colis à retourner"
        label-for="vi-colis-reference"
        description="Saisissez ou scannez la référence du colis à ajouter au retour inter-agence."
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="MaximizeIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            id="vi-colis-reference"
            ref="select"
            v-model="ref"
            placeholder="Référence du colis (ex : BL123456)"
            aria-label="Référence du colis à retourner"
            autofocus
            @change="checkOrder"
          />
        </b-input-group>
      </b-form-group>
    </b-card>

    <b-form-group
      v-if="user.role ==='admin' || user.role === 'superadmin'"
      label="Dépôt source"
      class="mt-1"
      aria-label="Sélection du dépôt source"
      description="Choisissez le dépôt d'origine pour ce retour inter-agence."
    >
      <validation-provider
        #default="{ errors }"
        name="repository"
      >
        <v-select
          v-model="sourceRepository"
          :clearable="false"
          placeholder="Sélectionner le dépôt source"
          label="name"
          :options="repositories"
          aria-label="Dépôt source"
          @input="getOrders"
        />
        <small
          class="text-danger"
          aria-live="polite"
        >{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>

    <!-- table -->
    <div
      class="d-flex justify-content-between"
      aria-label="Sélection des colis à retourner"
    >
      <b-card aria-label="Colis en attente de transfert">
        <h4 id="colis-attente-title">
          Colis en attente de transfert ({{ orders.length }})
        </h4>
        <p
          id="colis-attente-desc"
          class="mb-1 text-muted"
        >
          Sélectionnez les colis à inclure dans le bon de retour inter-agence.
        </p>
        <vue-good-table
          :columns="columns"
          :rows="orders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
          aria-labelledby="colis-attente-title"
          aria-describedby="colis-attente-desc"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Column: Action -->
            <span v-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  aria-label="Ajouter ce colis à la sélection"
                  tabindex="0"
                  role="button"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter à la sélection"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />
              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div
              class="d-flex justify-content-between flex-wrap"
              aria-label="Pagination des colis en attente"
            >
              <div class="d-flex align-items-center mb-0 mt-1">
                <span
                  class="text-nowrap"
                  aria-hidden="true"
                >Affichage 1 à</span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  aria-label="Nombre de résultats par page"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  aria-label="Changer de page"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>
      <b-card
        class="ml-1"
        aria-label="Colis sélectionnés pour le retour inter-agence"
      >
        <h4 id="colis-selectionnes-title">
          Colis sélectionnés ({{ selected.length }})
        </h4>
        <p
          id="colis-selectionnes-desc"
          class="mb-1 text-muted"
        >
          Vérifiez et ajustez la liste des colis à retourner.
        </p>
        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
          aria-labelledby="colis-selectionnes-title"
          aria-describedby="colis-selectionnes-desc"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >

            <!-- Column: Action -->
            <span v-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`invoice-row-${props.row.id}-show-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  aria-label="Retirer ce colis de la sélection"
                  tabindex="0"
                  role="button"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Retirer de la sélection"
                  class="cursor-pointer"
                  :target="`invoice-row-${props.row.id}-show-icon`"
                />
              </span>
            </span>

            <!-- Column: Common -->

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div
              class="d-flex justify-content-between flex-wrap"
              aria-label="Pagination des colis sélectionnés"
            >
              <div class="d-flex align-items-center mb-0 mt-1">
                <span
                  class="text-nowrap"
                  aria-hidden="true"
                >Affichage 1 à</span>
                <b-form-select
                  v-model="pageLength"
                  :options="['50','100','500']"
                  class="mx-1"
                  aria-label="Nombre de résultats par page"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
                <span class="text-nowrap "> {{ props.total }} résultats trouvés </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  aria-label="Changer de page"
                  @input="(value)=>props.pageChanged({currentPage:value})"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>

        </vue-good-table>
        <b-form-group
          label="Dépôt de destination"
          class="mt-1"
          aria-label="Sélection du dépôt de destination"
          description="Sélectionnez le dépôt de destination pour ce retour inter-agence."
        >
          <validation-provider
            #default="{ errors }"
            name="repository"
          >
            <v-select
              v-model="destinationRepository"
              :clearable="false"
              placeholder="Sélectionner le dépôt de destination"
              label="name"
              :options="repositories"
              aria-label="Dépôt de destination"
            />
            <small
              class="text-danger"
              aria-live="polite"
            >{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
        <b-form-group class="d-flex justify-content-end">
          <b-button
            variant="gradient-primary"
            class="ml-1 d-inline-block mt-1"
            :class="{ 'disabled-cursor': isLoading || !isReturnVoucherValid }"
            :disabled="isLoading || !isReturnVoucherValid"
            aria-label="Enregistrer le bon de retour inter-agence"
            @click="addReturnVoucher"
          >
            <feather-icon
              icon="PlusIcon"
              aria-hidden="true"
            />
            <span class="ml-1">Enregistrer</span>
          </b-button>
        </b-form-group>
      </b-card>

    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BPagination, BFormGroup, BFormInput, BInputGroup, BInputGroupPrepend, BFormSelect, BTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import ok from '@/assets/audio/ok.mp3'
import notexist from '@/assets/audio/notexist.mp3'

export default {
  name: 'CreateInterAgencyReturnVoucher',
  components: {
    ValidationProvider,
    BButton,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormSelect,
    BPagination,
    BTooltip,
    BCard,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      date: '01-10-2021',
      status: '',
      pageLength: 100,
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'BL',
          },
          width: '120px',
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher Gouvernorat',
          },
        },
        {
          label: 'Délégation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
        },
        {
          label: 'Adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
        {
          label: 'Telephone',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher telephone',
          },
        },
      ],
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      isLoading: false,
      repositories: [],
      destinationRepository: '',
      sourceRepository: '',
      ok,
      notexist,
    }
  },
  computed: {
    isReturnVoucherValid() {
      return (
        this.selected.length > 0
        && this.destinationRepository.id !== ''
        && this.sourceRepository !== ''
        && this.sourceRepository.id !== this.destinationRepository.id
      )
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getRepositories()

    if (this.user.role === 'manager') {
      this.sourceRepository = this.user.repository
      this.getOrders()
    }
  },
  mounted() {
    this.$refs.select.focus()
  },
  methods: {
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    async checkOrder() {
      const result = this.orders.find(element => element.id === (this.ref))
      if (result) {
        await this.addItem(result)
        this.ref = ''
        await this.playAudio(this.ok)
      } else {
        await this.withTitle('Colis non exist')
        await this.playAudio(this.notexist)
      }
    },
    async getOrders() {
      this.isLoading = true

      const payload = {
        repository: this.sourceRepository.id,
      }

      let url = 'api/orders/get-inter-agency-return'

      if (this.user.role === 'admin' || this.user.role === 'superadmin') {
        url += `?repository=${payload.repository}`
      } else if (this.user.role === 'manager') {
        payload.repository = undefined
      }

      const { data } = await axios.get(url, { params: payload })
      this.orders = data
      this.selected = []
      this.isLoading = false
    },
    async addReturnVoucher() {
      const obj = {
        orders: this.selected,
        destination_repository: this.destinationRepository.id,
        source_repository: this.sourceRepository.id,
        user: this.user,
      }
      this.isLoading = true
      try {
        await axios.post('/api/return-vouchers/create-inter-agency-return-voucher/', obj)
        this.$router.push('/bons-retours/inter-agence')
        this.showToast('success', 'top-center', 'Retour inter transfer ajouté avec succés')
        this.isLoading = false
      } catch (error) {
        this.showToast('danger', 'top-center', error.toString())
        this.isLoading = false
      }
    },
    async addItem(item) {
      this.selected.push(item)
      const i = this.orders.findIndex(element => element.id === item.id)
      this.orders.splice(i, 1)
    },
    async resetItem(item) {
      this.orders.push(item)
      const i = this.selected.findIndex(element => element.id === item.id)
      this.selected.splice(i, 1)
    },
    async getRepositories() {
      if (this.user.role === 'manager') {
        const { data } = await axios.get('/api/repositories/')
        const formatedRepositories = data.filter(repository => repository.id !== this.user.repository)
        this.repositories = formatedRepositories
      } else if (this.user.role === 'admin' || this.user.role === 'superadmin') {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>
<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
